import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState } from 'react';
import {
  MenuItem,
  FormControl,
  Box,
  Typography,
  Stack,
  InputLabel,
  Select,
  Divider,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  TaskTemplate,
  TaskWithTaskDependency,
  ValidTaskDependencyStatus,
} from '../../../../../shared/types/Task';
import { ActionButtons } from '../../../CancelSave';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutoComplete from '../../../AutoComplete/AutoComplete';

interface TaskDropDownProps {
  options: TaskTemplate[];
  description: string;
  taskTemplate: TaskTemplate;
  setHandle?: React.Dispatch<React.SetStateAction<string>>;
  setEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTaskDependency?: TaskWithTaskDependency;
  setLoadingData: React.Dispatch<React.SetStateAction<boolean>>;
  updateTaskDependency?: any
  addTaskDependency?: any
}

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      width: 580,
    },
  },
  anchorOrigin: {
    vertical: 44,
    horizontal: 120,
  },
  transformOrigin: {
    vertical: 1,
    horizontal: 120,
  },
};

const Placeholder = ({ children }: { children: string }) => {
  return <div style={{ color: '#222222', fontSize: typographyTokens.fontSizes.body }}>{children}</div>;
};

const ManageTaskDependency = ({
  options,
  description,
  taskTemplate,
  setHandle,
  setEdit,
  selectedTaskDependency,
  setLoadingData,
  updateTaskDependency,
  addTaskDependency
}: TaskDropDownProps) => {
  const [selectedTask, setSelectedTask] = useState(
    selectedTaskDependency?.id ?? 0
  );
  const [status, setStatus] = useState(
    selectedTaskDependency?.taskDependency.status ?? ''
  );
  const [hideDependentTask, setHideDependentTask] = useState(
    selectedTaskDependency?.taskDependency?.hideDependentTask ?? false
  );
  const [updateRAIE05Contact, setUpdateRAIE05Contact] = useState(
    selectedTaskDependency?.taskDependency?.updateRAIE05Contact ?? false
  );
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const [selectedTaskObj, setSelectedTaskObj] = useState(options.find((t) => t.id === (selectedTaskDependency?.id ?? 0)) || { name: '', id: 0 });
  const inputTaskRef = React.useRef(null);

  const handleTaskChange = (event: any) => {
    setSelectedTask(event.target.value);
  };

  const handlePhaseChange = (e: any) => {
    setStatus(e.target.value);
  };

  const handleHideDependentTaskChecked = (e: any) => {
    setHideDependentTask(e.target.checked);
  };

  const handleCancel = () => {
    if (selectedTaskDependency) {
      setSelectedTask(selectedTaskDependency.id);
      setStatus(selectedTaskDependency.taskDependency.status);
      setEdit && setEdit(false)
      setHideDependentTask(false);
      setUpdateRAIE05Contact(false)
    } else {
      setSelectedTask(0);
      setStatus('');
      setHandle && setHandle('');
      setHideDependentTask(false);
      setUpdateRAIE05Contact(false)
    }
  };

  const handleSave = () => {
    if (
      status === ValidTaskDependencyStatus.inReview ||
      status === ValidTaskDependencyStatus.complete
    ) {
      if (selectedTaskDependency) {
        const selectedTaskName = options.find(t => t.id === selectedTask)?.shortName || 'Task';
        updateTaskDependency({
          id: selectedTaskDependency.taskDependency.id,
          body: {
            taskID: selectedTask,
            dependentTaskID:
              selectedTaskDependency.taskDependency.dependentTaskID,
            status: status,
            hideDependentTask: hideDependentTask,
            updateRAIE05Contact,
            taskTemplateName: selectedTaskName
          },
        });
        handleCancel();
        setEdit && setEdit(false);
        setHideDependentTask(false);
        setUpdateRAIE05Contact(false);
      } else {
        setLoadingData(true);
        addTaskDependency({
          taskID: selectedTask,
          dependentTaskID: taskTemplate.id,
          status: status,
          hideDependentTask: hideDependentTask,
          updateRAIE05Contact,
          dependentTaskName: taskTemplate.shortName || taskTemplate.name
        });
        handleCancel();
        setHandle && setHandle('');
        setHideDependentTask(false);
        setUpdateRAIE05Contact(false);
      }
    }
  };
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#F8F9FA',
          padding: '16px 40px 16px 45px',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: typographyTokens.fontSizes.base,
            fontWeight: typographyTokens.fontWeights.bold,
          }}
        >
          {taskTemplate.name}  ({taskTemplate?.shortName})
        </Typography>        
      </Box>
      <Box mt={3} />
      <Box ml={4}>
        <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222' }}>Choose a Task</InputLabel>
      </Box>
      <FormControl sx={{ m: 4, mt: 1, width: '-webkit-fill-available' }} size="small">
        <TextField
          ref={inputTaskRef}
          size="small"
          value={selectedTaskObj.name || description}
          sx={{ width: { xs: '100%' } }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDownIcon />
              </InputAdornment>
            ),
          }}
          onClick={() => setShowTaskDropdown(true)}
        />
        <AutoComplete
          data={[...options].sort((a, b) => a.name.localeCompare(b.name))}
          type="task"
          show={showTaskDropdown}
          anchor={inputTaskRef.current}
          onClose={() => setShowTaskDropdown(false)}
          onChange={(e: any) => {
            setSelectedTaskObj(e);
            setShowTaskDropdown(false);
            setSelectedTask(e?.id ?? 0);
          }}
          showConfirmButton={false}
        />
      </FormControl>
      <Box m={2} />
      <Box ml={4}>
        <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>Required Status</InputLabel>
      </Box>
      <FormControl sx={{ m: 4, mt: 1, width: '-webkit-fill-available' }} size="small">
        <Select
          size="small"
          id="demo-simple-select-helper"
          name="Required Status"
          value={status}
          displayEmpty={true}
          onChange={(e) => handlePhaseChange(e)}
          sx={{ textTransform: 'capitalize' }}
        >
            <MenuItem disabled value="">
              <div style={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222', fontSize: typographyTokens.fontSizes.body }}>
                Choose a status from the dropdown
              </div>
            </MenuItem>
          {Object.values(ValidTaskDependencyStatus).map((e, i) => {
            return (
              <MenuItem key={i} value={e} sx={{ textTransform: 'capitalize' }}>
                {e === 'inReview' ? 'in Review' : e}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <FormControl sx={{ m: 4, mt: 1, width: '-webkit-fill-available' }} size="small">
        <FormControlLabel
          control={
            <Checkbox
              name="defer"
              onChange={(e) => handleHideDependentTaskChecked(e)}
              checked={hideDependentTask ?? false}
            />
          }
          label="Hide the parent task until the task is complete"
          sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222' }}
        />
      </FormControl>

      <FormControl sx={{ m: 4, mt: 1, width: '-webkit-fill-available' }} size="small">
        <FormControlLabel
          control={
            <Checkbox
              name="defer"
              onChange={(e) => setUpdateRAIE05Contact(e.target.checked)}
              checked={updateRAIE05Contact ?? false}
            />
          }
          label="Update RAIE05Contact when task is complete"
          sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}
        />
      </FormControl>

      <Divider />
      <Box m={2} />
      <ActionButtons
        handleCancel={handleCancel}
        handleSave={handleSave}
        buttonValidation={selectedTask === 0 || status === ''}
      />
    </>
  );
};

export default ManageTaskDependency;
